import { createStore, combineReducers } from 'redux';

/**
 * Constants
 */
export const storeConst = {
    ERROR_INVALID_ADD_REDUCER: `Invalid signature used for addReducer.
        Key should be string or symbol, type should be string or symbol and reducer should be a function.`,

    ADD_STORE_PART_ACTION: 'OMFE_CORE_ADD_STORE_PART_ACTION',
};

/**
 * Reducers in effect
 */
const reducers = {};
const typelessReducerKey = Symbol();
let currentReducerId = 0; // we can removeReducerById()


/**
 * The store to be exported
 * - hoisting problems
 * - gets referenced in applyReducers since promises need to dispatch their own result actions on it
 */
let tempStore = false;


/**
 * getReducersFor - returns registered reducers for a certain store key
 *
 * @param  {string} key = '' key for which to return registered reducers
 * @return {callable}          function to pass on to combineReducers
 */
export function getReducersFor(key = '') {
    function applyReducers(state = {}, action) {
        let newState = state;

        // handle action.type (basic action)
        if (typeof action.type === 'string' &&
            typeof reducers[key] === 'object'
        ) {
            // type-based reducers
            if (typeof reducers[key][action.type] === 'object') {
                for (const i of Object.keys(reducers[key][action.type])) {
                    if (typeof i === 'string' && typeof reducers[key][action.type][i] === 'function') {
                        newState = reducers[key][action.type][i](newState, action);
                    }
                }
            }

            // typeless reducers
            if (typeof reducers[key][typelessReducerKey] === 'object') {
                for (const i of Object.keys(reducers[key][typelessReducerKey])) {
                    if (typeof i === 'string' && typeof reducers[key][typelessReducerKey][i] === 'function') {
                        newState = reducers[key][typelessReducerKey][i](newState, action);
                    }
                }
            }
        }

        // what's left after all those mutations
        return newState;
    }
    return applyReducers;
}

/**
 * addReducer - Adds a reducer/returns a function that removes the reducer
 *
 * @param  {string}     key        state key to operate on
 * @param  {string}     [type]     action type to respond to
 * @param  {function}   reducer    the store reducer, signature is (prevState, action)
 * @return {function}              a function that removes the reducer (no arguments)
 */
const registeredStoreKeys = {};
export function addReducer(key, type, reducer) {
    if (
        (typeof key === 'string' && !key.length) ||
        (typeof key !== 'string' && typeof key !== 'symbol') ||
        (typeof type === 'string' && !type.length) ||
        (typeof type !== 'string' && typeof type !== 'symbol' &&
            typeof reducer === 'undefined' && typeof type !== 'function') ||
        ((typeof type === 'string' || typeof type === 'symbol') && typeof reducer !== 'function')
    ) {
        throw new Error(storeConst.ERROR_INVALID_ADD_REDUCER);
    }

    const actualType = typeof type === 'function' ? typelessReducerKey : type;
    const actualReducer = typeof type === 'function' ? type : reducer;

    if (typeof reducers[key] !== 'object' || reducers[key] === 'null') {
        reducers[key] = {};
    }

    if (typeof reducers[key][actualType] !== 'object' || reducers[key][actualType] === 'null') {
        reducers[key][actualType] = {};
    }

    // register this key to combineReducers in case we didn't have it
    if (!registeredStoreKeys[key]) {
        registeredStoreKeys[key] = getReducersFor(key);
        tempStore.replaceReducer(combineReducers(registeredStoreKeys));
    }

    const reducerId = currentReducerId++;
    reducers[key][actualType][reducerId] = actualReducer;
    let removed = false;

    // return said function
    return () => {
        if (!removed) {
            delete reducers[key][actualType][reducerId];
            removed = true;
            return true;
        }
        return false;
    };
}

/**
 * getMiddlewares - returns all the currently registered middlewares
 * - it's essentially a copy-paste from redux, the only difference is in the wrapped dispatch
 *
 * @return {function}  function that can be passed to createStore
 */
let middlewares = [];
let middlewaresChain = false;
export function getMiddlewares() {
    const compose = (...passedMiddlewares) => (...params) => {
        if (passedMiddlewares.length === 0) {
            return params[0];
        }

        const last = passedMiddlewares[passedMiddlewares.length - 1];
        const rest = passedMiddlewares.slice(0, -1);

        return rest.reduceRight((composed, f) => f(composed), last(...params));
    };

    return (createStoreParam) => (reducer, initialState, enhancer) => {
        const newStore = createStoreParam(reducer, initialState, enhancer);
        let dispatch = newStore.dispatch;

        const middlewareAPI = {
            getState: newStore.getState,
            dispatch: (action) => dispatch(action),
        };
        const recomposeChain = () => {
            const chain = middlewares.map(middleware => middleware(middlewareAPI));
            return compose(...chain)(newStore.dispatch);
        };

        dispatch = (...params) => {
            if (!middlewaresChain) {
                middlewaresChain = recomposeChain();
            }
            return middlewaresChain(...params);
        };

        return {
            ...newStore,
            dispatch,
        };
    };
}


/**
 * addMiddleware - registers a middleware; LIFO-like behavior
 *
 * @param  {function}   middleware  the middleware function
 * @return {bool/int}               false on failure, middlewares length on success
 */
export function addMiddleware(middleware) {
    if (typeof middleware !== 'function') {
        return false;
    }
    middlewares = [middleware].concat(middlewares);
    middlewaresChain = false;
    return middlewares.length;
}


/**
 * addStorePart - registers a new store part
 *
 * @param  {string} [STORE_PART = '']   store part (key)
 * @param  {object} initialState = {}   initial state for store part
 * @return {undefined}
 */
const storeParts = {};
export function addStorePart(STORE_PART = '', initialState = {}) {
    if (storeParts[STORE_PART]) {
        return false;
    }
    storeParts[STORE_PART] = true;
    const removeReducer = addReducer(STORE_PART, storeConst.ADD_STORE_PART_ACTION, (state = initialState) => {
        const nextState = Object.assign({}, state, initialState);
        removeReducer();
        return nextState;
    });
    return tempStore.dispatch({
        type: storeConst.ADD_STORE_PART_ACTION,
        STORE_PART,
        initialState,
    });
}


/**
 * Create a store and wrap the instance
 */
const reduxStore = createStore(getReducersFor(), getMiddlewares());
tempStore = {

    getStore() {
        return reduxStore;
    },

    getState() {
        return reduxStore.getState();
    },

    dispatch(action) {
        if (typeof action === 'object' && action !== null && typeof action.type === 'undefined') {
            const newAction = Object.assign({}, action);
            newAction.type = typelessReducerKey;
            return reduxStore.dispatch(newAction);
        }
        return reduxStore.dispatch(action);
    },

    subscribe(listener) {
        return reduxStore.subscribe(listener);
    },

    replaceReducer(nextReducer) {
        return reduxStore.replaceReducer(nextReducer);
    },
};


/**
 * We can finally export store
 */
const store = tempStore;
export { store };



// WEBPACK FOOTER //
// ./src/OMFECore/store.js